import React, { useState, useEffect, useMemo } from "react"
import moment from "moment"

import CatchModal from "~/library/CatchModal"
import { formatCurrency } from "~/utils/helpers"
import Sponsorship from "~/types/Sponsorship"
import PaymentItem from "~/types/PaymentItem"

import getAppConfig from "~/utils/getAppConfig"
import { apiPayments } from "~/routes"
import { useToast } from "~/utils/Toast"
import { calculateProcessingFeeCents } from "~/utils/processingFee"

type PaymentKind = "Invoice" | "Manual"

const MANUAL_METHODS = [
  { value: "cash", label: "Cash" },
  { value: "check", label: "Check" },
  { value: "bank_transfer", label: "Bank Transfer" },
  { value: "paypal", label: "PayPal" },
  { value: "other", label: "Other" },
]

interface PaymentModalProps {
  onClose: () => void
  onSuccess: () => void
  sponsorshipId: string
  sponsorship: Sponsorship
}

const PaymentModal: React.FC<PaymentModalProps> = ({
  onClose,
  onSuccess,
  sponsorshipId,
  sponsorship,
}) => {
  const toast = useToast()
  const { venue, venueSlug, processingFeeRates } = getAppConfig()

  const [paymentType, setPaymentType] = useState<PaymentKind>("Invoice")
  const [manualMethod, setManualMethod] = useState<string>("cash")

  const [paymentItems, setPaymentItems] = useState<PaymentItem[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    const initialItems: PaymentItem[] = []

    initialItems.push({
      description: `${sponsorship.company.name} (${sponsorship.area.name})`,
      quantity: 1,
      amountCents: sponsorship.price?.cents || 0,
    })

    setPaymentItems(initialItems)
  }, [sponsorship])

  const currencyIso = sponsorship.price?.currency_iso || venue.currency || "USD"
  const subtotalAmountCents = useMemo(
    () =>
      paymentItems
        .filter((item) => !item._destroy)
        .reduce((total, item) => total + item.quantity * item.amountCents, 0),
    [paymentItems]
  )
  const processingFeeCents = useMemo(() => {
    if (paymentType === "Manual") return 0

    return calculateProcessingFeeCents({
      catchMarkupFixedCents: venue.catchMarkupFixedCents,
      catchMarkupPercentage: venue.catchMarkupPercentage,
      currency: currencyIso,
      processingFeeRates,
      subtotalAmountCents,
    })
  }, [currencyIso, paymentType, processingFeeRates, subtotalAmountCents, venue])
  const customerPaysFee = venue.processingFeePayer === "fan_pays"

  const hasValidItems = useMemo(() => {
    return (
      paymentItems.filter((item) => !item._destroy && item.description && item.amountCents > 0)
        .length > 0
    )
  }, [paymentItems])

  const handleSubmit = () => {
    if (paymentItems.filter((item) => !item._destroy).length === 0) {
      toast("At least one payment item is required", "danger")
      return
    }

    setIsSubmitting(true)

    apiPayments
      .create({
        data: {
          venueSlug,
          payableId: sponsorshipId,
          payableType: "sponsorship",
          payment: {
            type: paymentType,
            paymentMethod: paymentType === "Manual" ? manualMethod : undefined,
            paymentItemsAttributes: paymentItems.filter((item) => !item._destroy),
          },
        },
      })
      .then(() => {
        toast(paymentType === "Manual" ? "Manual payment created" : "Invoice created", "success")
        onSuccess()
        onClose()
      })
      .catch((error) => {
        console.error("Failed to create payment:", error)
        toast(error.body?.errors?.join(", ") || "Failed to create payment", "danger")
      })
      .finally(() => setIsSubmitting(false))
  }

  const addPaymentItem = () => {
    setPaymentItems([
      ...paymentItems,
      {
        description: "",
        quantity: 1,
        amountCents: 0,
      },
    ])
  }

  const removePaymentItem = (index: number) => {
    const updatedItems = [...paymentItems]
    if (updatedItems[index].id) {
      updatedItems[index]._destroy = true
    } else {
      updatedItems.splice(index, 1)
    }
    setPaymentItems(updatedItems)
  }

  const updatePaymentItem = (index: number, field: keyof PaymentItem, value: any) => {
    const updatedItems = [...paymentItems]
    updatedItems[index] = { ...updatedItems[index], [field]: value }
    setPaymentItems(updatedItems)
  }

  const renderCompanyInfo = () => {
    const company = sponsorship?.company
    if (!company) {
      return (
        <div className="alert alert-warning">
          No company information available for this sponsorship.
        </div>
      )
    }

    return (
      <div className="row">
        <div className="col-12 mb-3">
          <h6 className="text-muted mb-2">BILL TO</h6>
          <div>
            <strong>{company.name}</strong>
            <br />
            {company.contactPerson && <span className="text-muted">{company.contactPerson}</span>}
            <br />
            {company.email && <span className="text-muted">{company.email}</span>}
            <br />
            {company.phone && <span className="text-muted">{company.phone}</span>}
          </div>
        </div>
      </div>
    )
  }

  const renderPaymentItems = () => {
    const visibleItems = paymentItems.filter((item) => !item._destroy)

    return (
      <div className="table-responsive">
        <table className="table table-sm">
          <thead>
            <tr>
              <th>Description</th>
              <th>Qty</th>
              <th>Unit Price</th>
              <th>Subtotal</th>
              <th style={{ width: "50px" }}></th>
            </tr>
          </thead>
          <tbody>
            {visibleItems.map((item) => {
              const actualIndex = paymentItems.indexOf(item)
              return (
                <tr key={actualIndex} className="align-middle">
                  <td>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={item.description}
                      onChange={(e) =>
                        updatePaymentItem(actualIndex, "description", e.target.value)
                      }
                      placeholder="Campaign Info"
                      required
                      disabled={isSubmitting}
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      value={item.quantity}
                      onChange={(e) =>
                        updatePaymentItem(actualIndex, "quantity", parseInt(e.target.value) || 1)
                      }
                      min="1"
                      style={{ width: "80px" }}
                      required
                      disabled={isSubmitting}
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      value={item.amountCents / 100}
                      onChange={(e) =>
                        updatePaymentItem(
                          actualIndex,
                          "amountCents",
                          Math.round((parseFloat(e.target.value) || 0) * 100)
                        )
                      }
                      step="1"
                      min="0"
                      style={{ width: "100px" }}
                      placeholder="0.00"
                      required
                      disabled={isSubmitting}
                    />
                  </td>
                  <td>
                    {formatCurrency({
                      cents: item.quantity * item.amountCents,
                      currency_iso: currencyIso,
                    })}
                  </td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => removePaymentItem(actualIndex)}
                      disabled={isSubmitting || visibleItems.length === 1}
                      title="Remove item"
                    >
                      x
                    </button>
                  </td>
                </tr>
              )
            })}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={3}>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary"
                  onClick={addPaymentItem}
                  disabled={isSubmitting}
                >
                  + Add Item
                </button>
              </td>
              <th>{formatCurrency({ cents: subtotalAmountCents, currency_iso: currencyIso })}</th>
              <td></td>
            </tr>
            {paymentType === "Invoice" && (
              <tr>
                <td colSpan={3} className="text-end">
                  {customerPaysFee
                    ? "Processing fee"
                    : "Processing fee (deducted from venue proceeds)"}
                </td>
                <th>{formatCurrency({ cents: processingFeeCents, currency_iso: currencyIso })}</th>
                <td></td>
              </tr>
            )}
            <tr>
              <td colSpan={3} className="text-end">
                Total
              </td>
              <th>
                {formatCurrency({
                  cents: subtotalAmountCents + (customerPaysFee ? processingFeeCents : 0),
                  currency_iso: currencyIso,
                })}
              </th>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>
    )
  }

  return (
    <CatchModal
      isOpen={true}
      onClose={onClose}
      title={paymentType === "Manual" ? "Record Manual Payment" : "Create Invoice"}
      size="lg"
    >
      <div className="space-y-4">
        {/* Type + Method */}
        <div className="row mb-3">
          <div className="col-12 d-flex gap-2 align-items-center">
            <label className="form-label mb-0 text-muted fw-bold">Type</label>
            <select
              className="form-select w-auto"
              value={paymentType}
              onChange={(e) => setPaymentType(e.target.value as PaymentKind)}
              disabled={isSubmitting}
            >
              <option value="Invoice">Invoice (Stripe)</option>
              <option value="Manual">Manual (record paid)</option>
            </select>

            {paymentType === "Manual" && (
              <>
                <label className="form-label mb-0 text-muted fw-bold ms-3">Method</label>
                <select
                  className="form-select w-auto"
                  value={manualMethod}
                  onChange={(e) => setManualMethod(e.target.value)}
                  disabled={isSubmitting}
                >
                  {MANUAL_METHODS.map((m) => (
                    <option key={m.value} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </select>
              </>
            )}
          </div>
        </div>

        {/* Header */}
        <div className="row mb-4">
          <div className="col-6">
            <h6 className="text-muted mb-2">
              {paymentType === "Manual" ? "PAYMENT FOR" : "INVOICE FOR"}
            </h6>
            <div>
              <strong>Sponsorship: {sponsorship?.area?.name}</strong>
              <br />
              <span className="text-muted">
                {moment(sponsorship?.startOn).format("MMMM Do, YYYY")} -{" "}
                {moment(sponsorship?.endOn).format("MMMM Do, YYYY")}
              </span>
              <br />
              {sponsorship?.areaType && (
                <span
                  className={`badge badge-sm ${
                    sponsorship.areaType === "physical" ? "bg-primary" : "bg-info"
                  }`}
                >
                  {sponsorship.areaType === "physical" ? "Physical" : "Digital"}
                </span>
              )}
            </div>
          </div>
          <div className="col-6">{renderCompanyInfo()}</div>
        </div>

        {/* Items */}
        <div>
          <h6 className="text-muted mb-3">
            {paymentType === "Manual" ? "PAYMENT ITEMS" : "ITEMS TO INVOICE"}
          </h6>
          {renderPaymentItems()}
        </div>

        {/* Actions */}
        <div className="d-flex gap-2 mt-4">
          <button
            type="button"
            onClick={onClose}
            className="btn btn-secondary ms-auto"
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            className="btn btn-primary"
            disabled={isSubmitting || !sponsorship?.company || !hasValidItems}
          >
            {isSubmitting
              ? "Saving..."
              : paymentType === "Manual"
              ? "Record Payment"
              : "Create Invoice"}
          </button>
        </div>
      </div>
    </CatchModal>
  )
}

export default PaymentModal
